/** Renders a structured CV JSON object into a professional A4-style preview.
 *  Keep in lockstep with the backend `CV_SYSTEM_PROMPT` schema (M2).
 *  Accepts an optional `template` (M5) → { engine, primary_color } to preview
 *  the chosen design in the editor. Defaults to Classic (brand orange). */
export default function CVPreview({ cv, template, className = "" }) {
  if (!cv) return null;
  const personal = cv.personal || {};
  const experience = cv.experience || [];
  const education = cv.education || [];
  const skills = cv.core_skills || cv.skills || [];
  const certifications = cv.certifications || [];
  const languages = cv.languages || [];
  const achievements = cv.achievements || [];

  const engine = template?.engine || "classic";
  const accent = template?.primary_color || "#F39319";
  const isModern = engine === "modern";
  const isCompact = engine === "compact";

  return (
    <article
      data-testid="cv-preview"
      data-testid-engine={engine}
      style={{ "--cv-accent": accent }}
      className={`bg-white text-neutral-900 rounded-2xl shadow-lg border border-border ${
        isCompact ? "p-6 md:p-8" : "p-8 md:p-12"
      } font-sans overflow-hidden ${className}`}
    >
      {isModern ? (
        <header
          className="rounded-xl -mx-2 -mt-2 mb-6 px-6 py-5"
          style={{ backgroundColor: accent, color: "#fff" }}
        >
          <h1 className="font-serif text-3xl md:text-4xl font-bold tracking-tight">
            {personal.full_name || "Your Name"}
          </h1>
          <p className="text-sm mt-2 flex flex-wrap gap-x-4 gap-y-1 opacity-90">
            {personal.email && <span>{personal.email}</span>}
            {personal.phone && <span>{personal.phone}</span>}
            {personal.location && <span>{personal.location}</span>}
            {personal.linkedin && <span>{personal.linkedin}</span>}
            {personal.portfolio && <span>{personal.portfolio}</span>}
          </p>
        </header>
      ) : (
        <header
          className="pb-4 mb-6"
          style={{ borderBottom: `1px solid ${engine === "classic" ? accent : "#e5e7eb"}` }}
        >
          <h1
            className={`font-serif font-bold tracking-tight ${
              isCompact ? "text-2xl md:text-3xl" : "text-3xl md:text-4xl"
            }`}
          >
            {personal.full_name || "Your Name"}
          </h1>
          <p className="text-sm mt-2 flex flex-wrap gap-x-4 gap-y-1 text-neutral-700">
            {personal.email && <span>{personal.email}</span>}
            {personal.phone && <span>{personal.phone}</span>}
            {personal.location && <span>{personal.location}</span>}
            {personal.linkedin && <span>{personal.linkedin}</span>}
            {personal.portfolio && <span>{personal.portfolio}</span>}
          </p>
        </header>
      )}

      {cv.professional_summary && (
        <section className="mb-6">
          <h2 className="text-[11px] tracking-[0.22em] uppercase font-bold mb-2" style={{ color: accent }}>
            Professional Summary
          </h2>
          <p className="text-sm leading-relaxed text-neutral-800">
            {cv.professional_summary}
          </p>
        </section>
      )}

      {skills.length > 0 && (
        <section className="mb-6">
          <h2 className="text-[11px] tracking-[0.22em] uppercase font-bold mb-3" style={{ color: accent }}>
            Core Skills
          </h2>
          <ul className="grid grid-cols-2 gap-x-6 gap-y-1 text-sm">
            {skills.map((s, i) => (
              <li key={i} className="flex gap-2 items-start">
                <span className="mt-1.5 h-1 w-1 rounded-full shrink-0" style={{ backgroundColor: accent }} />
                <span>{s}</span>
              </li>
            ))}
          </ul>
        </section>
      )}

      {experience.length > 0 && (
        <section className="mb-6">
          <h2 className="text-[11px] tracking-[0.22em] uppercase font-bold mb-3" style={{ color: accent }}>
            Experience
          </h2>
          <div className="space-y-5">
            {experience.map((role, i) => (
              <div key={i}>
                <div className="flex flex-wrap items-baseline justify-between gap-2">
                  <p className="font-serif text-lg font-semibold">
                    {role.title}
                    {role.company ? ` · ${role.company}` : ""}
                  </p>
                  <p className="text-xs text-neutral-600">
                    {[role.start_date, role.end_date].filter(Boolean).join(" — ")}
                    {role.location ? ` · ${role.location}` : ""}
                  </p>
                </div>
                {Array.isArray(role.bullets) && role.bullets.length > 0 && (
                  <ul className="mt-2 space-y-1 text-sm">
                    {role.bullets.map((b, j) => (
                      <li key={j} className="flex gap-2 items-start">
                        <span className="mt-2 h-1 w-1 rounded-full shrink-0" style={{ backgroundColor: accent }} />
                        <span>{b}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            ))}
          </div>
        </section>
      )}

      {education.length > 0 && (
        <section className="mb-6">
          <h2 className="text-[11px] tracking-[0.22em] uppercase font-bold mb-3" style={{ color: accent }}>
            Education
          </h2>
          <div className="space-y-3">
            {education.map((ed, i) => (
              <div key={i} className="flex flex-wrap items-baseline justify-between gap-2">
                <div>
                  <p className="font-serif text-base font-semibold">{ed.degree}</p>
                  <p className="text-sm text-neutral-700">
                    {ed.institution}
                    {ed.location ? `, ${ed.location}` : ""}
                    {ed.notes ? ` — ${ed.notes}` : ""}
                  </p>
                </div>
                <p className="text-xs text-neutral-600">
                  {[ed.start_date, ed.end_date].filter(Boolean).join(" — ")}
                </p>
              </div>
            ))}
          </div>
        </section>
      )}

      {(certifications.length > 0 || languages.length > 0 || achievements.length > 0) && (
        <section className="grid md:grid-cols-2 gap-6">
          {certifications.length > 0 && (
            <div>
              <h2 className="text-[11px] tracking-[0.22em] uppercase font-bold mb-2 " style={{ color: accent }}>
                Certifications
              </h2>
              <ul className="text-sm space-y-1">
                {certifications.map((c, i) => (
                  <li key={i}>{c}</li>
                ))}
              </ul>
            </div>
          )}
          {languages.length > 0 && (
            <div>
              <h2 className="text-[11px] tracking-[0.22em] uppercase font-bold mb-2 " style={{ color: accent }}>
                Languages
              </h2>
              <ul className="text-sm space-y-1">
                {languages.map((l, i) => (
                  <li key={i}>{l}</li>
                ))}
              </ul>
            </div>
          )}
          {achievements.length > 0 && (
            <div className="md:col-span-2">
              <h2 className="text-[11px] tracking-[0.22em] uppercase font-bold mb-2 " style={{ color: accent }}>
                Key Achievements
              </h2>
              <ul className="text-sm space-y-1">
                {achievements.map((a, i) => (
                  <li key={i} className="flex gap-2 items-start">
                    <span className="mt-2 h-1 w-1 rounded-full shrink-0" style={{ backgroundColor: accent }} />
                    <span>{a}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </section>
      )}
    </article>
  );
}
